import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const STATUS_COLOR = {
  on_point: "#34C759",
  available: "#34C759",
  with_customer: "#FF3B30",
  frozen: "#FFCC00",
  on_break: "#FFCC00",
  off_shift: "#3A3A3C",
};

const TowerTimer = ({ start, total }) => {
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 1000); return () => clearInterval(t); }, []);
  if (!start) return null;
  const elapsed = (Date.now() - new Date(start).getTime()) / 1000;
  const total_s = total * 60;
  const r = Math.max(0, total_s - elapsed);
  const m = Math.floor(r / 60);
  const s = Math.floor(r % 60);
  const danger = r < 60;
  return (
    <span className={`font-display tabular font-black text-7xl ${danger ? "text-[#FF3B30]" : "text-white"}`}>
      {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
};

export default function Tower() {
  const [queue, setQueue] = useState({ floor: [], phone: [] });
  const [opps, setOpps] = useState([]);
  const [score, setScore] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [q, o, s, d, a] = await Promise.all([
        api.get("/queue"),
        api.get("/opportunities"),
        api.get("/scoreboard"),
        api.get("/dealership"),
        api.get("/alerts"),
      ]);
      setQueue(q.data); setOpps(o.data); setScore(s.data); setDealer(d.data); setAlerts(a.data);
    } catch {}
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [refresh]);

  useWebSocket(() => refresh());

  const onPoint = queue.floor.find(e => e.status === "on_point");
  const phonePoint = queue.phone.find(e => e.status === "on_point");
  const unattended = alerts.find(a => a.type === "unattended_guest");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Unattended Guest full-screen */}
      {unattended && (
        <div className="fixed inset-0 bg-[#FF3B30] z-50 flex flex-col items-center justify-center alert-pulse">
          <div className="font-display text-[18vw] font-black uppercase tracking-tight leading-none">
            UNATTENDED
          </div>
          <div className="font-display text-6xl uppercase tracking-widest mt-4">
            GUEST · {unattended.location}
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="border-b border-white/10 px-6 py-3 flex items-center justify-between">
        <Link to="/board" className="flex items-center gap-2 text-white/40 hover:text-white text-xs uppercase tracking-widest" data-testid="exit-tower">
          <ArrowLeft className="w-4 h-4" /> Exit Tower
        </Link>
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" />
          <span className="font-display text-2xl font-black uppercase tracking-widest">{dealer?.name || "DEALERSHIP"} · LIVE FLOOR</span>
        </div>
        <div className="font-display text-2xl tabular font-black">{new Date().toLocaleTimeString()}</div>
      </div>

      <div className="grid grid-cols-12 gap-3 p-4">
        {/* Floor on point — DOMINANT */}
        <div className="col-span-7 bg-[#141414] border-2 border-[#34C759] onpoint-glow p-6 min-h-[40vh] flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#34C759] font-bold mb-3">Floor — On Point</div>
          {onPoint ? (
            <>
              <div className="font-display text-[12vw] font-black uppercase tracking-tighter leading-[0.85]">
                {onPoint.display_name || onPoint.name}
              </div>
              <div className="mt-auto flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-1">Power Rotation</div>
                  <TowerTimer start={onPoint.point_started_at} total={dealer?.power_rotation_minutes || 30} />
                </div>
                <div className="font-display text-[14vw] font-black tabular text-[#34C759] leading-none">1</div>
              </div>
            </>
          ) : (
            <div className="font-display text-6xl text-white/30 italic flex-1 flex items-center justify-center">No one on the floor</div>
          )}
        </div>

        {/* Right: scoreboard */}
        <div className="col-span-5 grid grid-cols-2 gap-3">
          {[
            { l: "Opps Today", v: score?.total_opportunities ?? 0, c: "#34C759" },
            { l: "Available", v: score?.available_now ?? 0 },
            { l: "Walk-Ins", v: score?.by_type?.walk_in ?? 0 },
            { l: "Appts", v: score?.by_type?.appointment ?? 0 },
            { l: "Phone Ups", v: score?.by_type?.phone_up ?? 0 },
            { l: "T.O. %", v: `${score?.to_rate ?? 0}%`, c: "#007AFF" },
          ].map(s => (
            <div key={s.l} className="bg-[#141414] border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-2">{s.l}</div>
              <div className="font-display tabular font-black text-7xl leading-none" style={{ color: s.c || "#fff" }}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Floor queue rest */}
        <div className="col-span-4 bg-[#141414] border border-white/10 p-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/40 font-bold mb-3">Floor Queue</div>
          <div className="space-y-2">
            {queue.floor.filter(e => e !== onPoint).slice(0, 8).map(e => (
              <div key={e.user_id} className="flex items-center gap-3">
                <span className="font-display tabular font-black text-3xl text-white/30 w-10 text-right">{e.position}</span>
                <span className="font-display font-bold uppercase tracking-wide text-xl flex-1 truncate">{e.display_name || e.name}</span>
                <span className="w-2 h-7" style={{ backgroundColor: STATUS_COLOR[e.status] }} />
              </div>
            ))}
          </div>
        </div>

        {/* Phone queue */}
        <div className="col-span-3 bg-[#141414] border border-white/10 p-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/40 font-bold mb-3">Phone Up</div>
          {phonePoint && (
            <div className="mb-3 p-3 border-2 border-[#007AFF]">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#007AFF]">Next Call</div>
              <div className="font-display font-black uppercase text-3xl">{phonePoint.display_name || phonePoint.name}</div>
            </div>
          )}
          <div className="space-y-2">
            {queue.phone.filter(e => e !== phonePoint).slice(0, 6).map(e => (
              <div key={e.user_id} className="flex items-center gap-2">
                <span className="font-display tabular font-bold text-xl text-white/30 w-7 text-right">{e.position}</span>
                <span className="font-display font-bold uppercase text-base flex-1 truncate">{e.display_name || e.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Activity */}
        <div className="col-span-5 bg-[#141414] border border-white/10 p-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/40 font-bold mb-3">Active Deals · {opps.length}</div>
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {opps.slice(0, 8).map(o => {
              const step = dealer?.process_steps?.[o.current_step_index] || "—";
              return (
                <div key={o.opp_id} className={`flex items-center gap-3 px-3 py-2 ${o.to_requested ? "bg-[#FF3B30]/20 border border-[#FF3B30]" : "bg-[#0A0A0A] border border-white/5"}`}>
                  <span className="font-display font-bold uppercase text-base w-24 truncate">{o.salesperson_name}</span>
                  <span className="text-xs text-white/60 flex-1 truncate">{o.customer_name || "—"}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#34C759]">{step}</span>
                </div>
              );
            })}
            {!opps.length && <div className="text-white/30 italic text-sm">No active deals</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
